import { Link } from "react-router-dom";

const METAL_BG =
  "linear-gradient(135deg,#FFFFFF 0%,#E4E6EA 22%,#A6ABB4 55%,#F1F2F4 78%,#898F99 100%)";

function LegalShell({ eyebrow, title, updated, children }) {
  return (
    <div
      className="min-h-screen text-neutral-200"
      data-testid="legal-page"
      style={{
        background:
          "radial-gradient(ellipse at 50% -10%, #202226 0%, #0e0f12 45%, #050506 85%, #000000 100%)",
        fontFamily: "'Archivo', system-ui, -apple-system, 'Segoe UI', sans-serif",
      }}
    >
      <header
        className="sticky top-0 z-10 backdrop-blur-md"
        style={{ borderBottom: "1px solid #22252A", background: "rgba(0,0,0,0.72)" }}
      >
        <div className="mx-auto max-w-3xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="legal-home-link">
            <div
              aria-hidden
              className="w-40 h-4"
              style={{
                background: METAL_BG,
                WebkitMaskImage: "url(/wellnet-logo.png)", maskImage: "url(/wellnet-logo.png)",
                WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                WebkitMaskSize: "contain", maskSize: "contain",
                WebkitMaskPosition: "left center", maskPosition: "left center",
              }}
            />
          </Link>
          <Link
            to="/"
            className="text-[10px] uppercase tracking-[0.3em] text-neutral-400 hover:text-neutral-100 transition-colors"
          >
            ← Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <div className="text-[10px] uppercase tracking-[0.4em] text-neutral-500 mb-4">{eyebrow}</div>
        <h1
          className="text-4xl sm:text-5xl font-black uppercase tracking-tight leading-[0.9]"
          style={{
            background: METAL_BG,
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            color: "transparent",
          }}
        >
          {title}
        </h1>
        {updated && (
          <div className="mt-4 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
            Last updated {updated}
          </div>
        )}
        <div className="mt-14 space-y-10 text-neutral-300 leading-relaxed text-base">
          {children}
        </div>
      </main>

      <footer style={{ borderTop: "1px solid #22252A" }}>
        <div className="mx-auto max-w-3xl px-6 py-8 flex flex-col sm:flex-row justify-between gap-3 text-[10px] uppercase tracking-[0.3em] text-neutral-500">
          <div>© 2026 Wellnet · Los Angeles</div>
          <div className="flex gap-6">
            <Link to="/support" className="hover:text-neutral-200 transition-colors">Support</Link>
            <Link to="/privacy" className="hover:text-neutral-200 transition-colors">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export function Support() {
  return (
    <LegalShell eyebrow="Wellnet · Help" title="Support" updated="December 2025">
      <Section title="Getting help">
        <p>
          Wellnet is a private wellness concierge. If you need assistance with your membership,
          an application, or account access, our team responds personally.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Email us at{" "}
          <a href="mailto:BG@wellnet.co" className="text-neutral-100 underline underline-offset-4 hover:text-white" data-testid="support-email">
            BG@wellnet.co
          </a>
          . We aim to respond within two business days.
        </p>
        <p className="text-sm text-neutral-500">
          For privacy or data requests, write to{" "}
          <a href="mailto:BG@wellnet.co" className="text-neutral-200 underline underline-offset-4 hover:text-white">BG@wellnet.co</a>.
        </p>
      </Section>

      <Section title="Membership applications">
        <p>
          Applications submitted through the site are reviewed by our team. If aligned, we reach
          out to arrange a short conversation. You can check the status of an application by
          replying to your confirmation email.
        </p>
      </Section>

      <Section title="Account access">
        <p>
          Members sign in from the homepage. If you have trouble accessing your account, use the
          “Forgot password” link in the sign-in window, or contact{" "}
          <a href="mailto:BG@wellnet.co" className="text-neutral-200 underline underline-offset-4 hover:text-white">BG@wellnet.co</a>.
        </p>
      </Section>
    </LegalShell>
  );
}

export function Privacy() {
  return (
    <LegalShell eyebrow="Wellnet · Legal" title="Privacy Policy" updated="December 2025">
      <Section title="Overview">
        <p>
          Wellnet (“we”, “us”) respects your privacy. This policy explains what information we
          collect when you use our website and membership services, how we use it, and your rights.
        </p>
      </Section>

      <Section title="Information we collect">
        <ul className="space-y-3">
          <li className="pb-3" style={{ borderBottom: "1px solid #22252A" }}><strong className="text-neutral-100">Application details</strong> — the name, email, phone number, referral, and message you submit when applying for membership.</li>
          <li className="pb-3" style={{ borderBottom: "1px solid #22252A" }}><strong className="text-neutral-100">Account information</strong> — email and a securely hashed password for members who create an account.</li>
          <li className="pb-3" style={{ borderBottom: "1px solid #22252A" }}><strong className="text-neutral-100">Technical data</strong> — standard log information such as IP address and browser type, used for security and to prevent abuse.</li>
        </ul>
      </Section>

      <Section title="How we use your information">
        <p>
          We use your information to review membership applications, communicate with you, provide
          and secure member accounts, and respond to your requests. We do not sell your personal
          information.
        </p>
      </Section>

      <Section title="How we share information">
        <p>
          We share information only with service providers that help us operate the service (for
          example, our email delivery provider used to notify us of new applications), and only as
          necessary. These providers are bound to protect your data.
        </p>
      </Section>

      <Section title="Data retention">
        <p>
          We keep application and account data for as long as needed to provide the service and to
          meet legal obligations. You may request deletion of your data at any time.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          You may request access to, correction of, or deletion of your personal information.
          To exercise these rights, contact{" "}
          <a href="mailto:BG@wellnet.co" className="text-neutral-100 underline underline-offset-4 hover:text-white" data-testid="privacy-email">
            BG@wellnet.co
          </a>.
        </p>
      </Section>

      <Section title="Contact">
        <p>
          Questions about this policy can be sent to{" "}
          <a href="mailto:BG@wellnet.co" className="text-neutral-200 underline underline-offset-4 hover:text-white">BG@wellnet.co</a>.
        </p>
      </Section>
    </LegalShell>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-sm uppercase tracking-[0.25em] mb-4 font-semibold text-neutral-100">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}
